import React, { useState, useEffect, useCallback } from 'react';
import { X, Search } from 'lucide-react';
import { ensureUsdtSymbol } from '@/utils/symbols';

interface TickerSymbolDrawerDesktopProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (normalizedSymbol: string) => void;
  title?: string;
  submitButtonText?: string;
}

const SHEET_WIDTH = 500;

export const TickerSymbolDrawerDesktop: React.FC<TickerSymbolDrawerDesktopProps> = ({
  open,
  onOpenChange,
  onSubmit,
  title = 'Enter Token Symbol',
  submitButtonText = 'Submit',
}) => {
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  
  // Animation states
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setInputValue('');
      setError(null);
    }
  }, [open]);

  // Animation effect
  useEffect(() => {
    if (open) {
      setIsVisible(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true);
        });
      });
      document.body.style.overflow = 'hidden';
    } else if (isVisible) {
      setIsAnimating(false);
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, 300);
      document.body.style.overflow = '';
      return () => clearTimeout(timer);
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) onOpenChange(false);
    };
    if (open) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open, onOpenChange]);

  const handleSubmit = useCallback(() => {
    const trimmed = inputValue.trim().toUpperCase();
    if (!trimmed) {
      setError('Please enter a token symbol');
      return;
    }
    
    // Validate - basic check for alphanumeric
    if (!/^[A-Z0-9]+$/.test(trimmed)) {
      setError('Invalid symbol format');
      return;
    }

    const normalized = ensureUsdtSymbol(trimmed);
    onSubmit(normalized);
    setInputValue('');
    setError(null);
  }, [inputValue, onSubmit]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Overlay with fade animation */}
      <div
        className="fixed inset-0 bg-black/60 z-[100] transition-opacity duration-300"
        style={{ opacity: isAnimating ? 1 : 0 }}
        onClick={() => onOpenChange(false)}
      />

      {/* Sheet with slide animation */}
      <div
        className="fixed top-0 right-0 h-full bg-secondary border-l border-border flex flex-col shadow-2xl z-[101] transition-transform duration-300 ease-out"
        style={{ 
          width: `${SHEET_WIDTH}px`,
          transform: isAnimating ? 'translateX(0)' : 'translateX(100%)',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-lg font-semibold text-text-primary">{title}</h2>
          <button
            onClick={() => onOpenChange(false)}
            className="p-2 rounded-lg hover:bg-tertiary text-text-secondary hover:text-text-primary transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          <div className="space-y-2">
            <label className="text-sm text-text-secondary">
              Token Symbol
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-text-secondary" />
              <input
                type="text"
                value={inputValue}
                onChange={(e) => {
                  setInputValue(e.target.value);
                  setError(null);
                }}
                onKeyDown={handleKeyDown}
                placeholder="e.g., BTC, ETH, SOL"
                className="w-full pl-10 pr-4 py-3 bg-primary border border-border rounded-lg text-text-primary placeholder-text-secondary focus:outline-none focus:border-accent transition-colors"
                autoFocus
              />
            </div>
            {error && (
              <p className="text-xs text-red-400">{error}</p>
            )}
          </div>

          <div className="bg-tertiary border border-border rounded-lg p-4 space-y-2">
            <p className="text-sm font-medium text-text-primary">Quick Tips</p>
            <ul className="text-xs text-text-secondary space-y-1">
              <li>• Enter the base symbol (e.g., BTC, ETH, SOL)</li>
              <li>• USDT pairing will be added automatically</li>
              <li>• Supports all major perpetual trading pairs</li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-secondary border-t border-border p-4">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!inputValue.trim()}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-lg text-white font-semibold bg-accent hover:bg-accent/80 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            {submitButtonText}
          </button>
        </div>
      </div>
    </>
  );
};
